import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import TablePaginationActions from './TablePaginationActions';
import TableToolbar from './TableToolbar';
import {
    useGlobalFilter,
    usePagination,
    useRowSelect,
    useSortBy,
    useTable,
    useAsyncDebounce,
    useFilters,
} from 'react-table';
import {
    Checkbox, TableBody, TableCell, TableContainer, TableHead, TableRow, TableFooter,
    TablePagination, TableSortLabel, Typography, Box,
    Table
} from '@mui/material';
import ReactLoading from 'react-loading';
import { Colors } from '../../styles/theme';
import { isEmptyObject } from '../../utils';
import { Block, CheckCircle } from '@mui/icons-material';
import ColumnHeaderWithFilter from './ColumnHeaderWithFilter';

const DEFAULT_PAGE_SIZE = 25;

const SelectableTable = ({
    columns,
    data,
    totalRowCount,
    fetchData,
    loading,
    pageCount: controlledPageCount,
    onRowSelected,
    initialGlobalFilter = '',
}) => {
    const {
        getTableProps,
        headerGroups,
        prepareRow,
        page,
        gotoPage,
        setPageSize,
        preGlobalFilteredRows,
        setGlobalFilter,
        state: { pageIndex, pageSize, selectedRowIds, globalFilter, sortBy },
    } = useTable(
        {
            columns,
            data,
            initialState: { 
                pageIndex: 0, 
                pageSize: DEFAULT_PAGE_SIZE,
                globalFilter: initialGlobalFilter 
            },
            pageCount: controlledPageCount,
            manualFilters: true,
            manualGlobalFilter: true,
            manualPagination: true,
            autoResetPage: false,
            manualSortBy: true,
        },
        useFilters,
        useGlobalFilter,
        useSortBy,
        usePagination,
        useRowSelect,
        hooks => {
            hooks.allColumns.push(columns => [
                ...columns,
                {
                    id: 'status',
                    Header: () => `Status`,
                    Cell: ({ row }) => row.original.status === 'active' ? <CheckCircle color='success' /> : <Block color='error' />,
                },
            ]);
        }
    );

    const fetchDataDebounced = useAsyncDebounce(fetchData, 500);

    useEffect(() => {
        fetchDataDebounced({ pageIndex, pageSize, globalFilter, sortBy });
    }, [fetchDataDebounced, pageIndex, pageSize, globalFilter, sortBy]);

    useEffect(() => {
        if (!isEmptyObject(selectedRowIds) && onRowSelected) {
            const selected = data.filter((_, i) => Object.keys(selectedRowIds).map(x => parseInt(x, 10)).includes(i)).map(x => x);
            onRowSelected(selected);
        }
    }, [selectedRowIds, data]);

    useEffect(() => {
        gotoPage(0);
    }, [globalFilter, sortBy]);

    const handleChangePage = (event, newPage) => {
        gotoPage(newPage);
    };

    const handleChangeRowsPerPage = event => {
        setPageSize(Number(event.target.value));
    };

    return (
        <TableContainer>
            <TableToolbar
                numSelected={Object.keys(selectedRowIds).length}
                preGlobalFilteredRows={preGlobalFilteredRows}
                setGlobalFilter={setGlobalFilter}
                globalFilter={globalFilter}
            />

            <Table {...getTableProps()} size='small'>
                <TableHead>
                    {headerGroups.map(headerGroup => (
                        <TableRow {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map(column => (
                                <TableCell
                                {...(column.id === 'selection'
                                    ? column.getHeaderProps()
                                    : column.getHeaderProps(column.getSortByToggleProps()))}
                            >
                                {column.id === 'selection' || column.id === 'action' || column.id === 'status' ? (
                                    column.render('Header')
                                ) : (
                                    <ColumnHeaderWithFilter column={column} />
                                )}
                            </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableHead>
                <TableBody>
                    {
                        loading ? <TableRow><TableCell colSpan={1000}>
                            <Box display={'flex'} justifyContent="center" alignItems={"center"}>
                                <ReactLoading type='bubbles' color={Colors.primary} />
                            </Box>
                        </TableCell></TableRow> :
                            page.length > 0 ?
                                page.map((row, i) => {
                                    prepareRow(row)
                                    return (
                                        <TableRow {...row.getRowProps()} sx={{textTransform: 'capitalize'}}>
                                            {row.cells.map(cell => {
                                                return (
                                                    <TableCell {...cell.getCellProps()}>
                                                        {cell.render('Cell')}
                                                    </TableCell>
                                                )
                                            })}
                                        </TableRow>
                                    )
                                }) :
                                <TableRow><TableCell colSpan={1000}>
                                    <Box display={'flex'} justifyContent="center" alignItems={"center"}>
                                        <Typography>No records found</Typography>
                                    </Box>
                                </TableCell></TableRow>
                    }
                </TableBody>

                <TableFooter>
                    <TableRow sx={{ width: '100%' }}>
                        <TablePagination
                            rowsPerPageOptions={[
                                DEFAULT_PAGE_SIZE,
                                50,
                                100,
                                200
                            ]}
                            count={totalRowCount}
                            rowsPerPage={pageSize}
                            page={pageIndex}
                            SelectProps={{
                                inputProps: { 'aria-label': 'rows per page' },
                                native: true,
                            }}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            ActionsComponent={TablePaginationActions}
                        />
                    </TableRow>
                </TableFooter>
            </Table>
        </TableContainer>
    );
};

SelectableTable.propTypes = {
    columns: PropTypes.array.isRequired,
    data: PropTypes.array.isRequired,
    totalRowCount: PropTypes.number.isRequired,
    fetchData: PropTypes.func.isRequired,
    loading: PropTypes.bool.isRequired,
    pageCount: PropTypes.number.isRequired,
    onRowSelected: PropTypes.func,
};

export default SelectableTable;